/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import Datagrid from '../../Datagrid';
import columns from './table_config';
import PropTypes from 'prop-types';
import SelectDrillDetails from '../../SelectDrillDetails';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import { EditOutlined } from '@mui/icons-material';
import TargetModal from '../TargetModal';
import WellboreModal from '../WellboreModalDialog';
import { HIDE_TARGET_ECD, HIDE_TARGET_IN_RUN_TYPE } from '../../../../utils/constants';
import { stringifyDeepEqual } from '../../../../utils/objectHelpers';
import styles from './styles';

const Wellbore = ({
  properties,
  setProperties,
  targetECD,
  anchorPoint,
  setAnchorPoint,
  setTargetECD,
  setTargetData,
  data,
  selectedJob,
  setWellbore,
  wellbore,
  setParameterDirty,
  checkWellbore,
  proxyLoader,
  hydraulicParameterData,
  setCheckWellbore,
  targetData,
  dialogParameter,
  initialTargetData,
  invalidWellInitRows
}) => {
  const apiRef = useGridApiRef();
  const hydraulics = useSelector((state) => state.hydraulics);
  const stripping = useSelector((state) => state.stripping);
  const rollover = useSelector((state) => state.rollover);
  const hydraulicsParameter = useSelector((state) => state.hydraulicsParameterAnalysis);
  const hydraulicsDisplacement = useSelector((state) => state.displacement);
  const observation = useSelector((state) => state.observation);
  const dispatch = useDispatch();
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showWellboreInitModal, setShowWellboreInitModal] = useState(false);
  const classes = styles();

  const handleAnchorPointTargetECD = (newAnchorPoint, newTargetECD) => {
    setAnchorPoint(newAnchorPoint);
    setTargetECD(newTargetECD);
  };

  useEffect(() => {
    if (!showTargetModal) {
      if (!stringifyDeepEqual(targetData, initialTargetData)) {
        updateTargetData(data);
      }
    }
  }, [showTargetModal]);

  const updateTargetData = (data) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === data?._id) {
          return {
            ...item,
            isDirty: true,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const updatedItem = {
            ...item,
            [field]: value,
            isDirty: value !== item[field],
          };

          if (
            field === 'hole_depth' &&
            (item.bit_depth === '' || item.bit_depth === null || item.bit_depth === undefined || item.bit_depth === item.hole_depth)
          ) {
            updatedItem.bit_depth = value;
          }

          return updatedItem;
        }
        return item;
      });

      return updatedProperties;
    });
  };

  const renderDrillstringCell = (params) => {
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={hydraulics?.drillDetails?.hydraulicsDrillDetails?.drillStrings}
        label={hydraulics?.drillDetails?.units?.MD?.label}
        decimals={hydraulics?.drillDetails?.units?.MD?.decimals}
      />
    );
  };

  const renderDrillFluidCell = (params) => {
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={hydraulics?.drillDetails?.hydraulicsDrillDetails?.drillFluids}
        label={hydraulics?.drillDetails?.units?.MD?.label}
        decimals={hydraulics?.drillDetails?.units?.MD?.decimals}
      />
    );
  };

  const renderTargetsEditAction = () => {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              variant="solid"
              disabled={observation.loading}
              onClick={() => {
                setShowTargetModal(true);
                dispatch({
                  type: REDUX_ACTIONS.OBSERVATION_LIST_LOADING,
                });
              }}
            >
              <EditOutlined />
            </IconButton>
            {HIDE_TARGET_ECD.includes(anchorPoint)
              ? (
                <Typography variant="button" fontSize={'1rem'} fontWeight={900}>None</Typography>
                )
              : (
                <Box display="flex" flexDirection="column">
                  <Typography variant="button" fontSize={'1rem'} fontWeight={900}>
                    {targetECD} {hydraulics.units.inputs.target_data.target_ecd.label}
                  </Typography>
                  <Typography variant="button" fontSize={'1rem'} fontWeight={900}>
                    @ {anchorPoint}
                  </Typography>
                </Box>
                )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderWellboreInitializationEditAction = () => {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              variant="solid"
              disabled={hydraulics?.details?.data[0]?.isNewRow || observation.loading}
              onClick={() => {
                setShowWellboreInitModal(true);
              }}
            >
              <EditOutlined />
            </IconButton>
            {((wellbore?.pipe?.length || wellbore?.annular?.length) && !checkWellbore) ? 'Applied' : 'None'}
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
    <Grid container maxWidth={HIDE_TARGET_IN_RUN_TYPE.includes(dialogParameter) ? '780px' : '1000px'}>
      <Grid item xs={12}>
        <Datagrid
          apiRef={apiRef}
          data={properties || []}
          sx={{
            marginLeft: '-6px',
            '.MuiDataGrid-cell': {
              fontSize: '1rem',
              fontWeight: 900,
              pointerEvents: proxyLoader ? 'none' : 'auto',
              backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
            },
            '.MuiDataGrid-columnHeader': {
              fontSize: '1.2rem',
              fontWeight: '900 !important'
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none',
            },
          }}
          loading={hydraulics?.details?.loading || rollover?.loading || stripping?.loading || hydraulicsParameter?.loading || hydraulicsDisplacement?.loading}
          columns={columns(renderDrillstringCell, renderDrillFluidCell, renderTargetsEditAction, renderWellboreInitializationEditAction, properties, hydraulicParameterData, dialogParameter)}
          onRowClick={(params, evt) => {
            evt.preventDefault();
            evt.stopPropagation();
          }}
          disableStickyHeader={false}
          isRowSelectable={() => !proxyLoader}
           getRowClassName={(params) => {
             return proxyLoader ? 'Mui-disabled' : (invalidWellInitRows[params?.id] ? classes.invalidRow : '');
           }}
          autoHeight={false}
          autoheight
          height={'150px'}
          pageSize={100}
          editMode="cell"
          toolbar={{
            options: {
              columns: false,
              filters: false,
              density: false,
              export: false
            },
          }}
          permissions={!properties[0]?.isNewRow && hydraulics?.details?.permissions}
        />
      </Grid>
    </Grid>
    {showTargetModal
      ? <TargetModal
              updateTargetData={updateTargetData}
              data={data}
              anchorPoint={anchorPoint}
              targetECD={targetECD}
              setTargetData={setTargetData}
              selectedJob={selectedJob}
              isOpen={showTargetModal}
              setAnchorPoint={setAnchorPoint}
              setTargetECD={setTargetECD}
              targetData={targetData}
              onSave={handleAnchorPointTargetECD}
              onCloseClick={() => {
                setShowTargetModal(false);
              }}
              setParameterDirty={setParameterDirty}
              runDetails={properties[0]}
            />
      : null
          }
          {showWellboreInitModal
            ? <WellboreModal
              data={data}
              isOpen={showWellboreInitModal}
              onCloseClick={() => {
                setShowWellboreInitModal(false);
              }}
              setWellbore={setWellbore}
              wellbore={wellbore}
              setParameterDirty={setParameterDirty}
              setCheckWellbore={setCheckWellbore}
              properties={properties}
            />
            : null
          }
        </>
  );
};
Wellbore.propTypes = {
  properties: PropTypes.array,
  setProperties: PropTypes.func,
  targetECD: PropTypes.any,
  anchorPoint: PropTypes.any,
  setAnchorPoint: PropTypes.func,
  setTargetECD: PropTypes.func,
  setTargetData: PropTypes.func,
  data: PropTypes.any,
  selectedJob: PropTypes.any,
  setWellbore: PropTypes.any,
  wellbore: PropTypes.any,
  setParameterDirty: PropTypes.any,
  checkWellbore: PropTypes.any,
  proxyLoader: PropTypes.any,
  hydraulicParameterData: PropTypes.any,
  setCheckWellbore: PropTypes.any,
  targetData: PropTypes.any,
  dialogParameter: PropTypes.any,
  initialTargetData: PropTypes.any,
  invalidWellInitRows: PropTypes.any
};

export default Wellbore;
